import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Trash2, Copy, Check, Mail, Users, Clock, Send, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminCodes = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  
  // Form pour code individuel
  const [showSingleForm, setShowSingleForm] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState(30);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data);
    } catch (error) {
      console.error('Erreur chargement codes:', error);
      toast.error('Erreur lors du chargement des codes');
    } finally {
      setLoading(false);
    }
  };

  const generateBatchCodes = async () => {
    setGeneratingCodes(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/codes/batch`,
        { count: 10, expires_in_days: 30 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} codes générés !`);
      fetchCodes();
    } catch (error) {
      console.error('Erreur génération codes:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingCodes(false);
    }
  };

  const createSingleCode = async (e) => {
    e.preventDefault();
    setGeneratingCodes(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/codes/single`,
        {
          client_name: clientName || null,
          client_email: clientEmail || null,
          expires_in_days: expiresInDays,
          send_email: Boolean(sendEmail && clientEmail)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.email_sent) {
        toast.success(`Code créé et envoyé à ${clientEmail} !`);
      } else {
        toast.success('Code créé !');
      }
      
      // Reset form
      setClientName('');
      setClientEmail('');
      setShowSingleForm(false);
      fetchCodes();
    } catch (error) {
      console.error('Erreur création code:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setGeneratingCodes(false);
    }
  };

  const resendEmail = async (codeId) => {
    setSendingEmail(codeId);
    try {
      await axios.post(
        `${API_URL}/admin/codes/${codeId}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email envoyé !');
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setSendingEmail(null);
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm('Supprimer ce code ?')) return;

    try {
      await axios.delete(`${API_URL}/admin/codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code supprimé');
      fetchCodes();
    } catch (error) {
      console.error('Erreur suppression code:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copié !');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  const availableCodes = codes.filter(c => !c.is_used && !isExpired(c.expires_at));
  const usedCodes = codes.filter(c => c.is_used);
  const expiredCodes = codes.filter(c => !c.is_used && isExpired(c.expires_at));

  return (
    <div className="min-h-screen pb-24 bg-sand-50" data-testid="admin-codes-page">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin')}
          className="text-white hover:bg-sage-600 mb-4"
          data-testid="admin-codes-back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-4xl font-serif font-light mb-2">Codes d'accès</h1>
        <p className="text-white/80 font-sans">Gestion des codes d'inscription clients</p>
      </div>

      {/* Stats */}
      <div className="px-6 py-6 grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <p className="text-xs text-sage-500 font-sans mb-1">Disponibles</p>
          <p className="text-2xl font-serif font-light text-sage-700" data-testid="admin-available-codes-count">
            {availableCodes.length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <p className="text-xs text-sage-500 font-sans mb-1">Utilisés</p>
          <p className="text-2xl font-serif font-light text-sage-700" data-testid="admin-used-codes-count">
            {usedCodes.length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <p className="text-xs text-sage-500 font-sans mb-1">Expirés</p>
          <p className="text-2xl font-serif font-light text-amber-600">
            {expiredCodes.length}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-2 space-y-3">
        {/* Bouton créer pour un client */}
        <Button
          onClick={() => setShowSingleForm(!showSingleForm)}
          className="w-full h-14 bg-declic hover:bg-declic/90 text-white rounded-xl"
          data-testid="create-single-code-btn"
        >
          <User className="w-5 h-5 mr-2" />
          Créer un code pour un client
        </Button>

        {/* Formulaire code individuel */}
        {showSingleForm && (
          <form onSubmit={createSingleCode} className="bg-white rounded-xl border border-sand-200 p-5 space-y-4">
            <div>
              <label className="text-sm text-sage-700 font-sans mb-1 block">Nom du client (optionnel)</label>
              <Input
                type="text"
                placeholder="Marie Dupont"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="border-sand-200 focus:border-sage-500"
              />
            </div>
            <div>
              <label className="text-sm text-sage-700 font-sans mb-1 block">Email du client (optionnel)</label>
              <Input
                type="email"
                placeholder="marie@example.com"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className="border-sand-200 focus:border-sage-500"
              />
            </div>
            <div>
              <label className="text-sm text-sage-700 font-sans mb-1 block">Validité (jours)</label>
              <Input
                type="number"
                min="1"
                max="365"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                className="border-sand-200 focus:border-sage-500 w-24"
              />
            </div>
            {clientEmail && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="rounded border-sage-300"
                />
                <label htmlFor="sendEmail" className="text-sm text-sage-700 font-sans">
                  Envoyer le code par email automatiquement
                </label>
              </div>
            )}
            <div className="flex space-x-3">
              <Button
                type="submit"
                disabled={generatingCodes}
                className="flex-1 bg-sage-500 hover:bg-sage-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                {generatingCodes ? 'Création...' : 'Créer le code'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSingleForm(false)}
              >
                Annuler
              </Button>
            </div>
          </form>
        )}

        {/* Bouton générer lot */}
        <Button
          onClick={generateBatchCodes}
          disabled={generatingCodes}
          variant="outline"
          className="w-full h-12 border-sage-300 text-sage-700 hover:bg-sage-50 rounded-xl"
          data-testid="admin-generate-batch-button"
        >
          <Users className="w-5 h-5 mr-2" />
          Générer un lot de 10 codes
        </Button>
      </div>

      {/* Available codes */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-xl border border-sand-200 p-5">
          <h3 className="font-sans font-medium text-sage-700 mb-4 flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Codes disponibles ({availableCodes.length})
          </h3>
          {availableCodes.length === 0 ? (
            <p className="text-center text-sage-500 font-sans py-8">Aucun code disponible</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {availableCodes.map((code) => (
                <div
                  key={code.id}
                  className="p-4 bg-sage-50 rounded-xl border border-sage-100"
                  data-testid={`admin-code-${code.code}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-lg text-sage-700 font-bold">{code.code}</span>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(code.code)}
                        className="text-sage-500 hover:text-sage-700 hover:bg-sage-100 h-8 w-8"
                      >
                        {copiedCode === code.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      {code.client_email && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => resendEmail(code.id)}
                          disabled={sendingEmail === code.id}
                          className="text-declic hover:text-declic/80 hover:bg-pink-50 h-8 w-8"
                          title="Envoyer par email"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteCode(code.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    {code.client_name && (
                      <span className="bg-white px-2 py-1 rounded-full text-sage-600 flex items-center">
                        <User className="w-3 h-3 mr-1" />
                        {code.client_name}
                      </span>
                    )}
                    {code.client_email && (
                      <span className="bg-white px-2 py-1 rounded-full text-sage-600 flex items-center">
                        <Mail className="w-3 h-3 mr-1" />
                        {code.client_email}
                      </span>
                    )}
                    {code.expires_at && (
                      <span className="bg-white px-2 py-1 rounded-full text-sage-600 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        Expire le {formatDate(code.expires_at)}
                      </span>
                    )}
                    {code.email_sent && (
                      <span className="bg-green-100 px-2 py-1 rounded-full text-green-700 flex items-center">
                        <Check className="w-3 h-3 mr-1" />
                        Email envoyé
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Used codes */}
      {usedCodes.length > 0 && (
        <div className="px-6 py-4">
          <div className="bg-white rounded-xl border border-sand-200 p-5">
            <h3 className="font-sans font-medium text-sage-700 mb-4 flex items-center">
              <span className="w-2 h-2 bg-sage-400 rounded-full mr-2"></span>
              Codes utilisés ({usedCodes.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {usedCodes.map((code) => (
                <div
                  key={code.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-70"
                >
                  <div>
                    <span className="font-mono text-sage-600">{code.code}</span>
                    {code.client_name && (
                      <span className="text-xs text-sage-500 ml-2">({code.client_name})</span>
                    )}
                  </div>
                  <span className="text-xs text-sage-500">
                    Utilisé le {formatDate(code.used_at)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Expired codes */}
      {expiredCodes.length > 0 && (
        <div className="px-6 py-4">
          <div className="bg-white rounded-xl border border-amber-200 p-5">
            <h3 className="font-sans font-medium text-amber-700 mb-4 flex items-center">
              <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
              Codes expirés ({expiredCodes.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {expiredCodes.map((code) => (
                <div
                  key={code.id}
                  className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                >
                  <div>
                    <span className="font-mono text-amber-700">{code.code}</span>
                    {code.client_name && (
                      <span className="text-xs text-amber-600 ml-2">({code.client_name})</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-amber-600">
                      Expiré le {formatDate(code.expires_at)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteCode(code.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Note */}
      <div className="px-6 py-4">
        <div className="bg-sage-50 rounded-xl p-4 border border-sage-100">
          <p className="text-sm text-sage-700 font-sans leading-relaxed">
            <strong>💡 Astuce :</strong> Créez un code personnalisé avec l'email du client pour lui envoyer automatiquement ses identifiants.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminCodes;
